import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";
import { useMutation } from "@apollo/client";
import { LinkButton } from "../Buttons";
import * as Icon from "react-feather";
import { H5, P } from "../Texts";
import DeleteModal from "../../organisms/DeleteModal";
import EditInitiativeModal from "../../organisms/EditInitiativeModal";
import { DELETE_INITIATIVE } from "../../../shared/queries";
import {
  getInitials,
  toastError,
  toastSuccess,
  toastWarning,
} from "../../../shared/helpers";

const InitiativeCardStyle = styled.div`
  border-radius: 4px;
  height: 200px;
  display: flex;
  padding: 24px 30px;
  flex-direction: column;
  justify-content: space-between;
`;

const DropDown = ({
  isOrganizer,
  orgId,
  initiativeId,
  toggleDropDown,
  toggleDeleteInitiativeModal,
  toggleEditInitiativeeModal,
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Only close if click is outside the dropdown and not on the toggle button
        if (!event.target.closest(".dropdown-toggle")) {
          toggleDropDown();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [toggleDropDown]);

  return (
    <div
      ref={dropdownRef}
      className="bg-white shadow-md py-2 absolute top-4 right-0 z-50"
    >
      <Link
        to={`/dashboard/organization/${orgId}/initiatives/${initiativeId}/pending-referrals`}
      >
        <LinkButton
          color="#000000"
          className="whitespace-no-wrap mb-2 text-left hover:bg-blue-100 w-full"
          p="4px 40px 4px 20px"
        >
          View
        </LinkButton>
      </Link>

      <LinkButton
        disabled={!isOrganizer}
        onClick={toggleEditInitiativeeModal}
        color="#000000"
        className="whitespace-no-wrap mb-2 text-left hover:bg-blue-100 w-full"
        p="4px 40px 4px 20px"
      >
        Edit
      </LinkButton>

      <LinkButton
        disabled={!isOrganizer}
        color="#D73636"
        className="whitespace-no-wrap text-left hover:bg-blue-100 w-full"
        p="4px 40px 4px 20px"
        onClick={toggleDeleteInitiativeModal}
      >
        Delete
      </LinkButton>
    </div>
  );
};

export const InitiativeCard = ({
  initiative: {
    name,
    address,
    id: initiativeId,
    service,
    images,
    description,
    organizationId,
    organizations,
    joinDate,
  },
  width,
}) => {
  const { id: orgId } = useParams();
  const [showInitiativeDropDown, setShowInitiativeDropDown] = useState(false);
  const [showDeleteInitiativeeModal, setShowDeleteInitiativeModal] = useState(false);
  const [showEditInitiativeeModal, setShowEditInitiativeModal] = useState(false);

  const isOrganizer = orgId === organizationId;
  const maxNumberOfAvatars = 3;

  const toggleDeleteInitiativeeModal = () =>
    setShowDeleteInitiativeModal(!showDeleteInitiativeeModal);

  const toggleEditInitiativeeModal = () =>
    setShowEditInitiativeModal(!showEditInitiativeeModal);

  const toggleInitiativeActionDropDown = () => {
    setShowInitiativeDropDown(!showInitiativeDropDown);
  };

  const handleDeleteInitiative = () => {
    toggleDeleteInitiativeeModal();
    toastWarning("Can not perforn this action on Demo mode");
  };

  return (
    <InitiativeCardStyle width={width} className="bg-white">
      <div>
        <div className="flex justify-between">
          <H5
            color="#000"
            fontFamily="semi"
            fontSize="18px"
            smallSize="16px"
            className="mb-0 font-semibold line-clamp-2 flex-grow"
          >
            {name}
          </H5>
          <div className="relative">
            <Icon.MoreVertical
              color="#25282B"
              size={18}
              className="flex-shrink-0 cursor-pointer dropdown-toggle"
              onClick={toggleInitiativeActionDropDown}
            />

            {showInitiativeDropDown && (
              <DropDown
                isOrganizer={isOrganizer}
                orgId={orgId}
                initiativeId={initiativeId}
                toggleDropDown={toggleInitiativeActionDropDown}
                toggleDeleteInitiativeModal={toggleDeleteInitiativeeModal}
                toggleEditInitiativeeModal={toggleEditInitiativeeModal}
              />
            )}
          </div>
        </div>

        <P
          color="#C4C4C4"
          fontSize="14px"
          smallSize="14px"
          className="mt-2 truncate"
        >
          {service?.location?.city}, {service?.location?.country}
        </P>
      </div>

      <div className="flex justify-between items-center">
        {organizations?.length > 0 ? (
          <div className="flex items-center gap-x-2">
            <div className="flex-1 flex -space-x-6">
              {organizations?.slice(0, maxNumberOfAvatars)?.map((item) => (
                <div
                  key={item.id}
                  className="w-8 h-8 relative p-5 text-sm uppercase bg-gray-200 border-2 border-white rounded-full flex items-center justify-center text-center text-black "
                >
                  {item?.logoUrl ? (
                    <img
                      className="h-full w-full object-cover "
                      src={item?.logoUrl}
                      alt={item.name}
                    />
                  ) : (
                    getInitials(item?.name)
                  )}
                </div>
              ))}

              {organizations?.length > maxNumberOfAvatars && (
                <div
                  style={{ fontSize: "10px" }}
                  className="z-30 w-8 h-8 p-5 bg-gray-300 border-2 border-white rounded-full flex items-center justify-center text-center text-black "
                >
                  +{organizations?.length - maxNumberOfAvatars}
                </div>
              )}
            </div>

            <P className="hidden xl:block" fontSize="12px" color="#5F5D77">
              Organizations
            </P>
          </div>
        ) : (
          <div></div>
        )}

        {joinDate && (
          <P color="#4F4F4F" fontSize="14px" smallSize="14px" className="truncate">
            {isOrganizer ? "Created" : "Joined"} on{" "}
            {moment(joinDate).format("Do MMM, YYYY")}
          </P>
        )}
      </div>

      {showEditInitiativeeModal && (
        <EditInitiativeModal
          initiative={{
            name: name,
            address: address,
            id: initiativeId,
            service: service,
            description: description,
            images: images,
          }}
          closeModal={toggleEditInitiativeeModal}
        />
      )}

      {showDeleteInitiativeeModal && (
        <DeleteModal
          header="Delete Initiative"
          closeModal={toggleDeleteInitiativeeModal}
          deleting={false}
          cta="Delete"
          action={handleDeleteInitiative}
          warning="Deleting this initiative is irreversible, are you certain of this action?"
        />
      )}
    </InitiativeCardStyle>
  );
};
